import { useState } from 'react';
import { Button } from '@/components/common';
import { useAuthStore } from '@/store';
import { apiClient } from '@/services/api';

interface ConsentStepProps {
  email: string;
  isAuthenticated: boolean;
  onContinue: () => void;
}

export function ConsentStep({ email, isAuthenticated, onContinue }: ConsentStepProps) {
  const updateUser = useAuthStore((s) => s.updateUser);
  const [optedIn, setOptedIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleContinue = async () => {
    setSubmitting(true);
    try {
      if (isAuthenticated) {
        // Send actual choice (true/false) so DB records the decision
        await apiClient.patch('/api/v1/auth/marketing-consent', { consent: optedIn });
        updateUser({ marketingConsent: optedIn, marketingConsentAsked: true });
      }
    } catch (err) {
      console.error('[Onboarding] Consent update failed:', err);
    }
    setSubmitting(false);
    onContinue();
  };

  return (
    <div className="onboarding-step">
      {/* Illustration */}
      <div style={{ textAlign: 'center', marginBottom: 'var(--space-6)' }}>
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
          <rect x="8" y="16" width="48" height="36" rx="4" stroke="var(--color-primary)" strokeWidth="2" fill="color-mix(in srgb, var(--color-primary) 10%, transparent)" />
          <path d="M8 20l24 16 24-16" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <h2 className="onboarding-card__headline">Get Weekly Job Search Tips</h2>
      <p className="onboarding-card__subline">
        We send practical advice on ATS optimization, resume trends, and job market updates.
        No spam, unsubscribe anytime.
      </p>

      {/* Value props */}
      <ul className="value-prop-list">
        <li className="value-prop-list__item">
          <svg className="value-prop-list__check" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          ATS score tips for your industry
        </li>
        <li className="value-prop-list__item">
          <svg className="value-prop-list__check" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          Interview preparation guides
        </li>
        <li className="value-prop-list__item">
          <svg className="value-prop-list__check" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          New features and job board support
        </li>
      </ul>

      {/* Email display */}
      {email && (
        <div
          style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-muted)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-surface-2)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-2)',
          }}
        >
          {email}
        </div>
      )}

      {/* Toggle */}
      <div
        className={`consent-toggle${optedIn ? ' consent-toggle--active' : ''}`}
        onClick={() => setOptedIn(!optedIn)}
        role="switch"
        aria-checked={optedIn}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOptedIn(!optedIn);
          }
        }}
      >
        <div className={`consent-toggle__track${optedIn ? ' consent-toggle__track--on' : ''}`}>
          <div className={`consent-toggle__thumb${optedIn ? ' consent-toggle__thumb--on' : ''}`} />
        </div>
        <div>
          <div className="consent-toggle__label">Send me tips and updates</div>
          <div className="consent-toggle__sublabel">About 2 emails per month</div>
        </div>
      </div>

      {/* Privacy note */}
      <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>
        We respect your inbox.{' '}
        <a href="/privacy" target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
          Privacy Policy
        </a>
      </p>

      {/* Actions */}
      <Button onClick={handleContinue} isLoading={submitting} fullWidth>
        Continue
      </Button>
    </div>
  );
}
